import React from 'react';
import { connect } from 'react-redux';
import { ImageSection } from 'app/components/ImageSection';
import { Button } from 'app/components/Button';
import { Typography, Paper, Grid } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { createStyles } from 'app/util/styles';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { getImagePathSafely } from 'app/util/images';

/**
 * Props for the mobile background wrapper.
 */
interface MobileBackgroundProps {
  /** Content rendered on top of the background image. */
  children: React.ReactNode;
  /** Resolved URL of the background image, consumed by the styles below. */
  imagePath: string;
}

/**
 * Styles for the mobile background. The image URL arrives via props (tss-react passes the component's props to the
 * styles callback), so the styled component can be created once at module scope.
 *
 * @param {any} theme - MUI theme.
 * @param {MobileBackgroundProps} props - Component props; only `imagePath` is read.
 * @returns {object} The `backgroundImage` rule.
 */
const mobileBackgroundStyles = (theme, { imagePath }: MobileBackgroundProps) =>
  createStyles({
    backgroundImage: {
      [theme.breakpoints.down('md')]: {
        background: `url('${imagePath}') no-repeat 50% center / cover`,
        height    : '440px',
        position  : 'relative',
      },
    },
  });

/**
 * Shows the referral landing image as a background on small screens.
 *
 * @param {object} props - Component props.
 * @param {Record<string, string>} props.classes - Class names generated from {@link mobileBackgroundStyles}.
 * @param {React.ReactNode} props.children - Content rendered on top of the background.
 * @returns {JSX.Element} The background container.
 */
const MobileBackground = ({ classes, children }: MobileBackgroundProps & { classes: Record<string, string> }) => {
  return (
    <div className={classes.backgroundImage}>
      {children}
    </div>
  );
};

/**
 * Styled mobile background wrapper. Created once at module scope so its component identity is stable: creating it
 * inside `ReferralLandingComponent` made a new component type on every render, remounting the whole page.
 */
const MobileWrapper: any = withStyles(MobileBackground, mobileBackgroundStyles);

export const ReferralLandingComponent = ({ ...props }: any) => {
  const { clientName, referralLandingImage, route, t } = props;
  const imagePath = getImagePathSafely(referralLandingImage);

  return (
    <MobileWrapper imagePath={imagePath}>
      <Grid
        container
        classes={{ container: 'canvas withoutForm mobileSplash' }}
      >
        <Grid item xs={12}>
          <Grid container classes={{ container: 'shadeCover mobileOnly' }}>
            <Grid item xs={12}>
              <Paper
                elevation = {2}
                classes   = {{
                  root      : 'formWrapper mobileTransparent',
                  elevation2: 'stopGate',
                }}
              >
                <ImageSection image={referralLandingImage}>
                  <Typography
                    variant = "h6"
                    classes = {{
                      root : 'mobileWhite mobileTextShadow',
                      h6: 'splashTitle titleMargin section',
                    }}
                  >
                    {t('referralLanding.title', { defaultValue: 'Get Pre-Qualified Now!' })}
                  </Typography>
                  <Typography
                    variant      = "body2"
                    gutterBottom = {true}
                    classes      = {{
                      root : 'mobileWhite mobileTextShadow',
                      body2: 'splashBody',
                    }}
                  >
                    {t('referralLanding.paragraph1', {
                      clientName,
                      defaultValue: '{{ clientName }}\'s free, online mortgage tool will help you estimate how much house you can afford and will deliver a pre-qualification letter directly to your Inbox in minutes.',
                    })}
                  </Typography>
                  <Button
                    fullWidth
                    variant   = "contained"
                    component = {Link}
                    to        = {route}
                    classes   = {{
                      root     : 'stackedButton',
                      fullWidth: 'tabletSizeLarge',
                      contained: 'tertiary',
                    }}
                    size = "large"
                  >
                    {t('referralLanding.button.start', { defaultValue: 'Get Started' })}
                  </Button>
                </ImageSection>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </MobileWrapper>
  );
};

const mapStateToProps = (state, ownProps) => ({
  referralLandingImage: state.config.stopGateReferralImage,
  clientName          : state.config.clientDisplayName,
  ...ownProps,
});

export const ReferralLanding = connect(
  mapStateToProps,
  null,
)(withTranslation()(ReferralLandingComponent));
